import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, RefreshCw, Search, ChevronUp, ChevronDown, UserCheck, Flag, MoreHorizontal, ClipboardList, AlertTriangle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  DropdownMenuSub,
  DropdownMenuSubTrigger,
  DropdownMenuSubContent,
} from "@/components/ui/dropdown-menu";
import { toast } from 'sonner';
import apiClient from '@/services/apiClient';

const STATUS_FILTERS = [
  { value: 'all', label: 'All Slips' },
  { value: 'pending', label: 'Pending' },
  { value: 'assigned', label: 'Assigned' },
  { value: 'in_progress', label: 'In Progress' },
  { value: 'completed', label: 'Completed' },
];

const STATUS_CONFIG = {
  pending: { label: 'Pending', className: 'bg-amber-100 text-amber-800 border-amber-200' },
  assigned: { label: 'Assigned', className: 'bg-blue-100 text-blue-800 border-blue-200' },
  in_progress: { label: 'In Progress', className: 'bg-violet-100 text-violet-800 border-violet-200' },
  completed: { label: 'Completed', className: 'bg-emerald-100 text-emerald-800 border-emerald-200' },
};

const PRIORITY_CONFIG = {
  high: { label: 'High', className: 'bg-red-100 text-red-700 border-red-200', dot: 'bg-red-500' },
  medium: { label: 'Medium', className: 'bg-orange-100 text-orange-700 border-orange-200', dot: 'bg-orange-500' },
  low: { label: 'Low', className: 'bg-yellow-100 text-yellow-700 border-yellow-200', dot: 'bg-yellow-500' },
  normal: { label: 'Normal', className: 'bg-gray-100 text-gray-600 border-gray-200', dot: 'bg-gray-400' },
};

const FGPutAwaySlipManagement = () => {
  const navigate = useNavigate();

  const [slips, setSlips] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [statusFilter, setStatusFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(0);
  const pageSize = 30;

  // Available staff for reassign
  const [availableStaff, setAvailableStaff] = useState([]);

  // Reassign dialog
  const [reassignDialogOpen, setReassignDialogOpen] = useState(false);
  const [selectedSlip, setSelectedSlip] = useState(null);
  const [selectedStaffId, setSelectedStaffId] = useState('');
  const [isReassigning, setIsReassigning] = useState(false);

  // Slip detail dialog
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  const [viewingSlip, setViewingSlip] = useState(null);

  // Stats
  const [stats, setStats] = useState({ pending: 0, assigned: 0, in_progress: 0, completed_today: 0 });

  // Queue info (unassigned pieces)
  const [queueInfo, setQueueInfo] = useState({ unassigned: 0, threshold: 120 });
  const [isGenerating, setIsGenerating] = useState(false);

  // Mismatch data
  const [mismatchInfo, setMismatchInfo] = useState(null);

  const fetchSlips = useCallback(async () => {
    setIsLoading(true);
    try {
      const params = new URLSearchParams();
      if (statusFilter !== 'all') params.append('status', statusFilter);
      if (searchQuery.trim()) params.append('search', searchQuery.trim());
      params.append('limit', pageSize);
      params.append('skip', page * pageSize);

      const response = await apiClient.get(`/wms/fg/put-away/slips?${params.toString()}`);
      setSlips(response.data.slips || []);
      setTotalCount(response.data.total || 0);
    } catch (error) {
      console.error('Failed to load slips:', error);
      toast.error('Failed to load slips');
    } finally {
      setIsLoading(false);
    }
  }, [statusFilter, searchQuery, page]);

  const fetchAvailableStaff = useCallback(async () => {
    try {
      const response = await apiClient.get('/wms/fg/put-away/available-staff');
      setAvailableStaff(response.data.available_staff || []);
    } catch (error) {
      console.error('Failed to load available staff:', error);
    }
  }, []);

  const fetchStats = useCallback(async () => {
    try {
      const response = await apiClient.get('/wms/fg/put-away/dashboard-stats');
      setStats(response.data.slips || { pending: 0, assigned: 0, in_progress: 0, completed_today: 0 });
    } catch (error) {
      console.error('Failed to load stats:', error);
    }
  }, []);

  const fetchQueueInfo = useCallback(async () => {
    try {
      const response = await apiClient.get('/wms/fg/put-away/queue-status');
      setQueueInfo({
        unassigned: response.data.pieces?.unassigned || 0,
        threshold: response.data.pieces?.threshold || 120,
        ready: response.data.pieces?.ready_for_slip || false,
      });
    } catch (error) {
      console.error('Failed to load queue info:', error);
    }
  }, []);

  const fetchMismatchInfo = useCallback(async () => {
    try {
      const response = await apiClient.get('/wms/fg/put-away/mismatch-scan');
      setMismatchInfo(response.data);
    } catch (error) {
      console.error('Failed to load mismatch info:', error);
    }
  }, []);

  useEffect(() => {
    fetchSlips();
    fetchAvailableStaff();
    fetchStats();
    fetchQueueInfo();
    fetchMismatchInfo();
  }, [fetchSlips, fetchAvailableStaff, fetchStats, fetchQueueInfo, fetchMismatchInfo]);

  // Reset page when filter/search changes
  useEffect(() => {
    setPage(0);
  }, [statusFilter, searchQuery]);

  const handleSetPriority = async (slipId, priority) => {
    try {
      await apiClient.post(`/wms/fg/put-away/set-priority/${slipId}`, { priority });
      toast.success(`Priority set to ${priority}`);
      fetchSlips();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to set priority');
    }
  };

  const handleGenerateSlips = async () => {
    setIsGenerating(true);
    try {
      const response = await apiClient.post('/wms/fg/put-away/generate-all-pending');
      const data = response.data;
      if (data.generated > 0) {
        toast.success(`Generated ${data.generated} slip(s). ${data.unassigned_pieces_remaining} pieces remaining.`);
        fetchSlips();
        fetchStats();
        fetchQueueInfo();
      } else {
        toast.info(data.message || 'No slips to generate');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to generate slips');
    } finally {
      setIsGenerating(false);
    }
  };

  const handleOpenReassign = (slip) => {
    setSelectedSlip(slip);
    setSelectedStaffId('');
    setReassignDialogOpen(true);
  };

  const handleReassign = async () => {
    if (!selectedSlip || !selectedStaffId) return;
    setIsReassigning(true);
    try {
      const endpoint = selectedSlip.assigned_to
        ? `/wms/fg/put-away/reassign-slip/${selectedSlip.slip_id}`
        : `/wms/fg/put-away/assign-slip/${selectedSlip.slip_id}`;
      await apiClient.post(endpoint, { staff_id: selectedStaffId });
      toast.success(`Slip ${selectedSlip.assigned_to ? 'reassigned' : 'assigned'} successfully`);
      setReassignDialogOpen(false);
      fetchSlips();
      fetchStats();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reassign');
    } finally {
      setIsReassigning(false);
    }
  };

  const handleViewDetail = async (slip) => {
    try {
      const response = await apiClient.get(`/wms/fg/put-away/slip/${slip.slip_id}`);
      setViewingSlip(response.data);
      setDetailDialogOpen(true);
    } catch (error) {
      toast.error('Failed to load slip details');
    }
  };

  const formatDateTime = (iso) => {
    if (!iso) return '-';
    try {
      const d = new Date(iso);
      return d.toLocaleString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit', hour12: true });
    } catch { return iso; }
  };

  const getStatusBadge = (status) => {
    const config = STATUS_CONFIG[status] || { label: status, className: 'bg-gray-100 text-gray-700' };
    return <Badge variant="outline" className={`text-xs font-medium ${config.className}`} data-testid={`status-badge-${status}`}>{config.label}</Badge>;
  };

  const getPriorityBadge = (priority) => {
    const p = priority || 'normal';
    const config = PRIORITY_CONFIG[p] || PRIORITY_CONFIG.normal;
    return (
      <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-medium border ${config.className}`} data-testid={`priority-badge-${p}`}>
        <span className={`w-1.5 h-1.5 rounded-full ${config.dot}`}></span>
        {config.label}
      </span>
    );
  };

  const totalPages = Math.ceil(totalCount / pageSize);

  return (
    <div className="min-h-screen bg-[#FFFBF5]">
      {/* Header */}
      <header className="bg-[#1A1F2E] text-[#F9FAFB] shadow-lg">
        <div className="max-w-[1400px] mx-auto px-4 md:px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button
              variant="ghost"
              onClick={() => navigate('/wms/fg')}
              className="text-[#F9FAFB] hover:bg-white/10"
              data-testid="back-btn"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <ClipboardList className="w-7 h-7" />
            <div>
              <h1 className="text-lg font-bold font-display">Put Away Slip Management</h1>
              <p className="text-xs text-[#E8DCC8]">View, prioritize & reassign slips</p>
            </div>
          </div>
          <Button
            variant="outline"
            onClick={() => { fetchSlips(); fetchStats(); fetchQueueInfo(); fetchMismatchInfo(); }}
            className="border-white/20 text-[#F9FAFB] hover:bg-white/10"
            data-testid="refresh-btn"
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${isLoading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>
      </header>

      <main className="max-w-[1400px] mx-auto px-4 md:px-6 py-6">
        {/* Stats Row */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
          <Card className="border-[#E5DFD0]">
            <CardContent className="p-4">
              <p className="text-xs text-[#5A5F6E] uppercase tracking-wide">Pending</p>
              <p className="text-2xl font-bold text-amber-600 mt-1" data-testid="stat-pending">{stats.pending || 0}</p>
            </CardContent>
          </Card>
          <Card className="border-[#E5DFD0]">
            <CardContent className="p-4">
              <p className="text-xs text-[#5A5F6E] uppercase tracking-wide">Assigned</p>
              <p className="text-2xl font-bold text-blue-600 mt-1" data-testid="stat-assigned">{stats.assigned || 0}</p>
            </CardContent>
          </Card>
          <Card className="border-[#E5DFD0]">
            <CardContent className="p-4">
              <p className="text-xs text-[#5A5F6E] uppercase tracking-wide">In Progress</p>
              <p className="text-2xl font-bold text-violet-600 mt-1" data-testid="stat-in-progress">{stats.in_progress || 0}</p>
            </CardContent>
          </Card>
          <Card className="border-[#E5DFD0]">
            <CardContent className="p-4">
              <p className="text-xs text-[#5A5F6E] uppercase tracking-wide">Completed Today</p>
              <p className="text-2xl font-bold text-emerald-600 mt-1" data-testid="stat-completed">{stats.completed_today || 0}</p>
            </CardContent>
          </Card>
          <Card className={`border-[#E5DFD0] ${queueInfo.unassigned >= queueInfo.threshold ? 'border-red-300 bg-red-50/50' : ''}`}>
            <CardContent className="p-4">
              <p className="text-xs text-[#5A5F6E] uppercase tracking-wide">Unassigned Pieces</p>
              <div className="flex items-center justify-between mt-1">
                <p className={`text-2xl font-bold ${queueInfo.unassigned >= queueInfo.threshold ? 'text-red-600' : 'text-[#5A5F6E]'}`} data-testid="stat-unassigned">
                  {queueInfo.unassigned}
                </p>
                {queueInfo.unassigned >= queueInfo.threshold && (
                  <Button
                    size="sm"
                    onClick={handleGenerateSlips}
                    disabled={isGenerating}
                    className="bg-red-600 hover:bg-red-700 text-white text-xs h-7 px-2"
                    data-testid="generate-slips-btn"
                  >
                    {isGenerating ? 'Generating...' : 'Generate Slips'}
                  </Button>
                )}
              </div>
              <p className="text-[10px] text-[#5A5F6E] mt-1">Threshold: {queueInfo.threshold}</p>
            </CardContent>
          </Card>
        </div>

        {/* Mismatch Alert */}
        {mismatchInfo && mismatchInfo.affected_slips > 0 && (
          <div className="mb-5 border border-amber-300 bg-amber-50 rounded-lg p-4 flex items-start gap-3" data-testid="mismatch-alert">
            <AlertTriangle className="w-5 h-5 text-amber-600 mt-0.5 flex-shrink-0" />
            <div className="flex-1">
              <p className="text-sm font-semibold text-amber-800">USN Mismatch Detected</p>
              <p className="text-xs text-amber-700 mt-1">
                {mismatchInfo.affected_slips} completed slips have foreign USNs placed.
                {mismatchInfo.already_in_fg_inventory > 0 
                  ? ` ${mismatchInfo.total_expected_usns_not_placed} expected USNs not in slip records, but ${mismatchInfo.already_in_fg_inventory} already found in FG inventory.`
                  : ` ${mismatchInfo.total_expected_usns_not_placed} expected USNs were never placed.`}
                {mismatchInfo.truly_unplaced > 0 && (
                  <span className="font-semibold"> Truly unplaced: {mismatchInfo.truly_unplaced}</span>
                )}
              </p>
            </div>
            <Button
              size="sm"
              onClick={() => navigate('/wms/fg/put-away-clearance')}
              className="bg-emerald-600 hover:bg-emerald-700 text-white text-xs flex-shrink-0"
              data-testid="table-clearance-btn"
            >
              Table Clearance
            </Button>
          </div>
        )}

        {/* Filters & Search */}
        <div className="flex flex-col md:flex-row gap-3 mb-5">
          <div className="flex gap-2 flex-wrap">
            {STATUS_FILTERS.map(f => (
              <Button
                key={f.value}
                variant={statusFilter === f.value ? 'default' : 'outline'}
                size="sm"
                onClick={() => setStatusFilter(f.value)}
                className={statusFilter === f.value ? 'bg-[#1A1F2E] text-white hover:bg-[#2a2f3e]' : 'border-[#E5DFD0] text-[#5A5F6E] hover:bg-[#F5EFE0]'}
                data-testid={`filter-${f.value}`}
              >
                {f.label}
              </Button>
            ))}
          </div>
          <div className="flex-1" />
          <div className="relative w-full md:w-72">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#5A5F6E]" />
            <Input
              placeholder="Search slip ID or staff name..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-9 border-[#E5DFD0] bg-white"
              data-testid="search-input"
            />
          </div>
        </div>

        {/* Table */}
        <Card className="border-[#E5DFD0] overflow-hidden">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="bg-[#F5EFE0] border-b border-[#E5DFD0]">
                  <TableHead className="text-[#1A1F2E] font-semibold text-xs w-[140px]">Slip ID</TableHead>
                  <TableHead className="text-[#1A1F2E] font-semibold text-xs">Assigned Staff</TableHead>
                  <TableHead className="text-[#1A1F2E] font-semibold text-xs w-[100px]">Status</TableHead>
                  <TableHead className="text-[#1A1F2E] font-semibold text-xs w-[90px]">Priority</TableHead>
                  <TableHead className="text-[#1A1F2E] font-semibold text-xs text-center w-[70px]">Pieces</TableHead>
                  <TableHead className="text-[#1A1F2E] font-semibold text-xs text-center w-[70px]">Designs</TableHead>
                  <TableHead className="text-[#1A1F2E] font-semibold text-xs w-[130px]">Created</TableHead>
                  <TableHead className="text-[#1A1F2E] font-semibold text-xs w-[130px]">Assigned At</TableHead>
                  <TableHead className="text-[#1A1F2E] font-semibold text-xs text-center w-[80px]">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {isLoading ? (
                  <TableRow>
                    <TableCell colSpan={9} className="h-32 text-center text-[#5A5F6E]">
                      <RefreshCw className="w-5 h-5 animate-spin mx-auto mb-2" />
                      Loading slips...
                    </TableCell>
                  </TableRow>
                ) : slips.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={9} className="h-32 text-center text-[#5A5F6E]">
                      No slips found for the selected filter.
                    </TableCell>
                  </TableRow>
                ) : (
                  slips.map((slip) => (
                    <TableRow
                      key={slip.slip_id}
                      className="border-b border-[#E5DFD0] hover:bg-[#FEFCF8] cursor-pointer transition-colors"
                      onClick={() => handleViewDetail(slip)}
                      data-testid={`slip-row-${slip.slip_id}`}
                    >
                      <TableCell className="font-mono text-xs font-medium text-[#1A1F2E]">{slip.slip_id}</TableCell>
                      <TableCell className="text-sm text-[#1A1F2E]">
                        {slip.assigned_to_name || slip.assigned_to || <span className="text-[#B0A898] italic">Unassigned</span>}
                      </TableCell>
                      <TableCell>{getStatusBadge(slip.status)}</TableCell>
                      <TableCell>{getPriorityBadge(slip.priority)}</TableCell>
                      <TableCell className="text-center text-sm font-medium">{slip.total_pieces || 0}</TableCell>
                      <TableCell className="text-center text-sm font-medium">{slip.total_designs || 0}</TableCell>
                      <TableCell className="text-xs text-[#5A5F6E]">{formatDateTime(slip.created_at)}</TableCell>
                      <TableCell className="text-xs text-[#5A5F6E]">{formatDateTime(slip.assigned_at)}</TableCell>
                      <TableCell className="text-center" onClick={(e) => e.stopPropagation()}>
                        {slip.status !== 'completed' && (
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="ghost" size="sm" className="h-8 w-8 p-0" data-testid={`actions-btn-${slip.slip_id}`}>
                                <MoreHorizontal className="w-4 h-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end" className="w-48">
                              <DropdownMenuSub>
                                <DropdownMenuSubTrigger className="text-sm">
                                  <Flag className="w-3.5 h-3.5 mr-2" />
                                  Set Priority
                                </DropdownMenuSubTrigger>
                                <DropdownMenuSubContent>
                                  <DropdownMenuItem onClick={() => handleSetPriority(slip.slip_id, 'high')} data-testid={`priority-high-${slip.slip_id}`}>
                                    <span className="w-2 h-2 rounded-full bg-red-500 mr-2"></span> High
                                  </DropdownMenuItem>
                                  <DropdownMenuItem onClick={() => handleSetPriority(slip.slip_id, 'medium')} data-testid={`priority-medium-${slip.slip_id}`}>
                                    <span className="w-2 h-2 rounded-full bg-orange-500 mr-2"></span> Medium
                                  </DropdownMenuItem>
                                  <DropdownMenuItem onClick={() => handleSetPriority(slip.slip_id, 'low')} data-testid={`priority-low-${slip.slip_id}`}>
                                    <span className="w-2 h-2 rounded-full bg-yellow-500 mr-2"></span> Low
                                  </DropdownMenuItem>
                                  <DropdownMenuItem onClick={() => handleSetPriority(slip.slip_id, 'normal')} data-testid={`priority-normal-${slip.slip_id}`}>
                                    <span className="w-2 h-2 rounded-full bg-gray-400 mr-2"></span> Normal
                                  </DropdownMenuItem>
                                </DropdownMenuSubContent>
                              </DropdownMenuSub>
                              <DropdownMenuSeparator />
                              <DropdownMenuItem onClick={() => handleOpenReassign(slip)} data-testid={`reassign-btn-${slip.slip_id}`}>
                                <UserCheck className="w-3.5 h-3.5 mr-2" />
                                {slip.assigned_to ? 'Reassign' : 'Assign'}
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        )}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between px-4 py-3 border-t border-[#E5DFD0] bg-[#FEFCF8]">
              <p className="text-xs text-[#5A5F6E]">
                Showing {page * pageSize + 1}-{Math.min((page + 1) * pageSize, totalCount)} of {totalCount}
              </p>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  disabled={page === 0}
                  onClick={() => setPage(p => p - 1)}
                  className="border-[#E5DFD0]"
                  data-testid="prev-page"
                >
                  <ChevronUp className="w-4 h-4 rotate-[-90deg]" /> Prev
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={page >= totalPages - 1}
                  onClick={() => setPage(p => p + 1)}
                  className="border-[#E5DFD0]"
                  data-testid="next-page"
                >
                  Next <ChevronDown className="w-4 h-4 rotate-[-90deg]" />
                </Button>
              </div>
            </div>
          )}
        </Card>
      </main>

      {/* Reassign Dialog */}
      <Dialog open={reassignDialogOpen} onOpenChange={setReassignDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-[#1A1F2E] font-display">
              {selectedSlip?.assigned_to ? 'Reassign' : 'Assign'} Slip
            </DialogTitle>
            <DialogDescription className="text-sm text-[#5A5F6E]">
              Select a staff member from today's FG Put Away workers.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <p className="text-sm text-[#5A5F6E] mb-1">Slip ID</p>
              <p className="font-mono text-sm font-medium text-[#1A1F2E]" data-testid="reassign-slip-id">{selectedSlip?.slip_id}</p>
            </div>
            {selectedSlip?.assigned_to_name && (
              <div>
                <p className="text-sm text-[#5A5F6E] mb-1">Currently Assigned To</p>
                <p className="text-sm font-medium text-[#1A1F2E]">{selectedSlip.assigned_to_name}</p>
              </div>
            )}
            <div>
              <p className="text-sm text-[#5A5F6E] mb-2">Select Staff (Today's FG Put Away Workers)</p>
              <Select value={selectedStaffId} onValueChange={setSelectedStaffId}>
                <SelectTrigger data-testid="staff-select">
                  <SelectValue placeholder="Select a staff member" />
                </SelectTrigger>
                <SelectContent>
                  {availableStaff.length === 0 ? (
                    <SelectItem value="none" disabled>No staff available today</SelectItem>
                  ) : (
                    availableStaff.map(s => (
                      <SelectItem key={s.staff_id} value={s.staff_id} data-testid={`staff-option-${s.staff_id}`}>
                        {s.staff_name || s.staff_id}
                      </SelectItem>
                    ))
                  )}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setReassignDialogOpen(false)} className="border-[#E5DFD0]" data-testid="reassign-cancel">
              Cancel
            </Button>
            <Button
              onClick={handleReassign}
              disabled={!selectedStaffId || isReassigning}
              className="bg-[#1A1F2E] hover:bg-[#2a2f3e] text-white"
              data-testid="reassign-confirm"
            >
              {isReassigning ? 'Processing...' : (selectedSlip?.assigned_to ? 'Reassign' : 'Assign')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Slip Detail Dialog */}
      <Dialog open={detailDialogOpen} onOpenChange={setDetailDialogOpen}>
        <DialogContent className="sm:max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-[#1A1F2E] font-display">
              Slip Details: {viewingSlip?.slip_id}
            </DialogTitle>
            <DialogDescription className="text-sm text-[#5A5F6E]">
              Full details and items for this put away slip.
            </DialogDescription>
          </DialogHeader>
          {viewingSlip && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <div>
                  <p className="text-xs text-[#5A5F6E]">Status</p>
                  <div className="mt-1">{getStatusBadge(viewingSlip.status)}</div>
                </div>
                <div>
                  <p className="text-xs text-[#5A5F6E]">Priority</p>
                  <div className="mt-1">{getPriorityBadge(viewingSlip.priority)}</div>
                </div>
                <div>
                  <p className="text-xs text-[#5A5F6E]">Assigned To</p>
                  <p className="text-sm font-medium mt-1">{viewingSlip.assigned_to_name || viewingSlip.assigned_to || '-'}</p>
                </div>
                <div>
                  <p className="text-xs text-[#5A5F6E]">Total Pieces</p>
                  <p className="text-sm font-medium mt-1">{viewingSlip.total_pieces || 0}</p>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <p className="text-xs text-[#5A5F6E]">Created</p>
                  <p className="text-sm mt-1">{formatDateTime(viewingSlip.created_at)}</p>
                </div>
                <div>
                  <p className="text-xs text-[#5A5F6E]">Assigned At</p>
                  <p className="text-sm mt-1">{formatDateTime(viewingSlip.assigned_at)}</p>
                </div>
              </div>

              {/* Items Table */}
              {viewingSlip.items && viewingSlip.items.length > 0 && (
                <div>
                  <p className="text-sm font-semibold text-[#1A1F2E] mb-2">Items ({viewingSlip.items.length})</p>
                  <div className="border border-[#E5DFD0] rounded-lg overflow-hidden">
                    <Table>
                      <TableHeader>
                        <TableRow className="bg-[#F5EFE0]">
                          <TableHead className="text-xs">Design</TableHead>
                          <TableHead className="text-xs">Shade</TableHead>
                          <TableHead className="text-xs text-center">Qty</TableHead>
                          <TableHead className="text-xs">Processed By</TableHead>
                          <TableHead className="text-xs">Bin</TableHead>
                          <TableHead className="text-xs">Status</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {viewingSlip.items.map((item, idx) => (
                          <TableRow key={item.item_id || idx} className="border-b border-[#E5DFD0]">
                            <TableCell className="text-xs">{item.design_name || '-'}</TableCell>
                            <TableCell className="text-xs">{item.shade || '-'}</TableCell>
                            <TableCell className="text-xs text-center">{item.qty || 0}</TableCell>
                            <TableCell className="text-xs text-[#5A5F6E]">{item.processed_by_name || item.processed_by_staff || '-'}</TableCell>
                            <TableCell className="text-xs font-mono">{item.suggested_bin || item.bin_code || '-'}</TableCell>
                            <TableCell>{getStatusBadge(item.status)}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Fix Mismatches Dialog removed — Table Clearance handles everything */}
    </div>
  );
};

export default FGPutAwaySlipManagement;
